// ReelPopupAd — Facebook/Reels-style auto-popup advertiser banner shown when a video/reel post plays.
// Previously fetched from /banner-ad and recorded revenue via /ads/impression-v2 +
// /campaigns/{id}/click -- the older, disconnected flat-payout ad system (db.campaigns).
// Now calls the real Business Suite system directly (db.ad_campaigns via /business/ad/*),
// the same 40% platform / 40% creator-pool / 20% viewer-pool escrow model SponsorBanner uses.
// No wallet writes happen on impression/click -- pools are divided pro-rata and paid out
// once, at campaign end, via /business/campaigns/{id}/settle.
import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, ExternalLink } from "lucide-react";

export default function ReelPopupAd({ visible, onClose, creatorId }) {
  const [ad, setAd] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setDismissed(false);
    api.get("/business/ad/next", { params: { surface: "reel", creator_id: creatorId } })
      .then(({ data }) => setAd(data || null))
      .catch(() => setAd(null));
  }, [visible, creatorId]);

  // Record one impression each time an ad becomes visible for this reel (server-side
  // caps and dedupes per viewer+campaign+creator -- see /business/ad/impression).
  useEffect(() => {
    if (!visible || !ad?.id) return;
    api.post("/business/ad/impression", { campaign_id: ad.id, creator_id: creatorId || null, surface: "reel" }).catch(() => {});
  }, [visible, ad?.id, creatorId]);

  if (!visible || !ad || dismissed) return null;

  const click = async () => {
    try { await api.post("/business/ad/click", { campaign_id: ad.id, creator_id: creatorId || null }); } catch { /* ignore */ }
    if (ad.cta_url) window.open(ad.cta_url, "_blank", "noopener");
  };

  return (
    <div
      className="absolute left-0 right-0 bottom-0 z-30 px-3 pb-3 animate-fade-in-up"
      data-testid="reel-popup-ad"
    >
      <div
        className="glass-strong border border-nexus-amber/40 flex items-center gap-3 p-3"
        style={{ background: "linear-gradient(135deg, #0a0e14 0%, rgba(255,193,7,0.12) 100%)" }}
      >
        {ad.creative_url && (
          <img src={ad.creative_url} alt="" className="w-12 h-12 object-cover ring-1 ring-white/20 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] tracking-[0.3em] uppercase text-nexus-amber font-display">// sponsored</div>
          <div className="text-sm font-semibold truncate text-white">{ad.name}</div>
          {ad.headline && <div className="text-[11px] text-white/70 truncate">{ad.headline}</div>}
        </div>
        {ad.cta_url && (
          <button
            onClick={click}
            className="text-[10px] tracking-[0.2em] uppercase font-display px-3 py-2 border border-nexus-amber text-nexus-amber hover:bg-nexus-amber/10 transition-colors flex items-center gap-1 flex-shrink-0"
            data-testid="reel-ad-cta"
          >
            {ad.cta_text || "Learn more"} <ExternalLink className="w-3 h-3" />
          </button>
        )}
        <button
          onClick={() => { setDismissed(true); onClose?.(); }}
          className="text-white/40 hover:text-white p-1"
          data-testid="reel-ad-close"
          aria-label="Dismiss ad"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
